/**
 * <ImportDashboard /> — the 120px persistent header at the top of
 * every active ImportSessionPage AND every PastImportDetailPage.
 *
 * Per BUILD_READINESS_M29.md acceptance criterion 9-22 + the
 * commandment-lock from spec section 1: cannot be hidden, cannot
 * be collapsed. Status visibility is non-negotiable.
 *
 * Layout (top to bottom, ~120px total):
 *   Row 1 (28px):  Stepper bar (6 phases)
 *   Row 2 (24px):  AI status line — ✦ sparkle + what AI is doing
 *   Row 3 (24px):  Progress bar (only when measurable)
 *   Row 4 (44px):  3 KPI cards (Valid / Attention / Blocking)
 *   Row 5 (12px):  Cost meter — bottom-right
 *
 * Composes all 5 sub-components:
 *   <ImportStepperBar />
 *   <ImportStatusLine />
 *   <ImportProgressBar />
 *   <ImportKPICards />
 *   <ImportCostMeter />
 *
 * Historical mode (`isHistorical=true`) renders the same surface
 * with the final state — sparkle stops pulsing, status line says
 * "Completed [timestamp]" or "Failed: [reason]".
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { ImportSessionDTO } from "../../lib/api";
import { ImportCostMeter } from "./ImportCostMeter";
import { ImportKPICards } from "./ImportKPICards";
import { ImportProgressBar } from "./ImportProgressBar";
import { ImportStatusLine } from "./ImportStatusLine";
import { ImportStepperBar, type ImportPhase } from "./ImportStepperBar";

/** What the dashboard derives from the session row's
 *  `dashboard_state` JSONB (see ImportSession schema). */
export interface DashboardStateView {
  current_phase?: ImportPhase | null;
  /** 0-1, null when the current task isn't measurable. */
  progress_fraction?: number | null;
  /** Seconds. Null when unknown. */
  eta_seconds?: number | null;
  /** What AI is doing right now — shown to the right of ✦. */
  sparkle_message?: string | null;
  /** Status tone — "working" pulses; everything else is static. */
  sparkle_tone?: "working" | "thinking" | "waiting" | "error";
  /** KPI counts — defaults to zeros if absent. */
  kpis?: {
    valid_count?: number;
    attention_count?: number;
    blocking_count?: number;
    /** Optional one-line breakdown under the Attention card. */
    attention_breakdown?: string | null;
  };
}

export interface ImportDashboardProps {
  session: ImportSessionDTO;
  /** When true, render in read-only "past import" mode:
   *  sparkle stops pulsing, status line shows completion timestamp. */
  isHistorical?: boolean;
  /** Optional right-aligned action slot — e.g. an Abandon button. */
  trailing?: ReactNode;
  locale?: "en" | "ar";
}

const STATUS_TO_PHASE: Record<string, ImportPhase> = {
  uploaded: "upload",
  understanding: "understanding",
  awaiting_clarification: "conversation",
  reviewing_proposal: "review",
  committing: "commit",
  completed: "done",
  failed: "done",
  abandoned: "done",
};

export function ImportDashboard({
  session,
  isHistorical = false,
  trailing,
  locale = "en",
}: ImportDashboardProps) {
  const { t } = useTranslation();
  const state =
    (session.dashboard_state as DashboardStateView | undefined) ?? {};

  const phase: ImportPhase =
    state.current_phase ?? STATUS_TO_PHASE[session.status] ?? "upload";

  const message =
    state.sparkle_message ??
    _defaultMessage(session.status, isHistorical, session.completed_at);

  const tone =
    state.sparkle_tone ??
    _defaultTone(session.status, isHistorical);

  const kpis = state.kpis ?? {};
  const valid = kpis.valid_count ?? 0;
  const attention = kpis.attention_count ?? 0;
  const blocking = kpis.blocking_count ?? 0;
  const totalForPct = session.total_rows_parsed || null;

  // Idle = historical mode OR awaiting HR input OR terminal status
  const idle =
    isHistorical ||
    session.status === "awaiting_clarification" ||
    session.status === "completed" ||
    session.status === "failed" ||
    session.status === "abandoned" ||
    session.status === "uploaded";

  return (
    <section
      className="rounded-xl border border-border-subtle bg-surface-default p-3 sm:p-4 space-y-3"
      aria-label={t("app.imp_dashboard_aria")}
    >
      <ImportStepperBar current={phase} locale={locale} />
      <ImportStatusLine
        message={message}
        idle={idle}
        tone={tone}
        trailing={trailing}
      />
      {state.progress_fraction !== undefined &&
        state.progress_fraction !== null && (
          <ImportProgressBar
            fraction={state.progress_fraction}
            etaSeconds={state.eta_seconds ?? null}
          />
        )}
      <ImportKPICards
        valid={valid}
        attention={attention}
        blocking={blocking}
        attentionBreakdown={kpis.attention_breakdown ?? null}
        total={totalForPct}
      />
      <div className="flex justify-end">
        <ImportCostMeter
          spentUsd={session.ai_cost_so_far_usd}
          budgetUsd={session.ai_budget_usd}
        />
      </div>
    </section>
  );
}

function _defaultMessage(
  status: string,
  isHistorical: boolean,
  completedAt: string | null,
): string {
  if (status === "completed") {
    return completedAt
      ? `Completed ${_formatCompletion(completedAt)}`
      : "Completed";
  }
  if (status === "failed") return "Failed — see details below";
  if (status === "abandoned") return "Abandoned";
  if (isHistorical) return "Past import";
  return {
    uploaded: "File uploaded — waiting to start analysis",
    understanding: "✦ Analyzing your data structure…",
    awaiting_clarification: "Waiting for your input",
    reviewing_proposal: "Review my proposal below",
    committing: "Writing approved rows to your tenant…",
  }[status] ?? "Working…";
}

function _defaultTone(
  status: string,
  isHistorical: boolean,
): "working" | "thinking" | "waiting" | "error" {
  if (status === "failed") return "error";
  if (status === "awaiting_clarification") return "waiting";
  if (isHistorical) return "waiting";
  if (status === "understanding" || status === "committing") return "working";
  return "thinking";
}

function _formatCompletion(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
